import React from 'react';
import { FieldErrors, UseFormRegister, UseFormSetValue, UseFormWatch } from 'react-hook-form';

import InputField from '@/components/atoms/InputField';
import TextAreaField from '@/components/atoms/TextAreaField';
import DatePickerField from '@/components/atoms/DatePickerField';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

/** Step 1 fields use nullable defaults in the form; validated by `workActivityStep1Schema`. */
export type WorkActivityStepFormValues = {
  activity_name: string;
  description: string;
  activity_date: Date | null;
  duration_hours: number | string;
  duration_minutes: number | string;
};

interface CreateWorkActivityStepProps {
  register: UseFormRegister<WorkActivityStepFormValues>;
  errors: FieldErrors<WorkActivityStepFormValues>;
  watch: UseFormWatch<WorkActivityStepFormValues>;
  setValue: UseFormSetValue<WorkActivityStepFormValues>;
  clearErrors: (name?: keyof WorkActivityStepFormValues | (keyof WorkActivityStepFormValues)[]) => void;
}

const CreateWorkActivityStep: React.FC<CreateWorkActivityStepProps> = ({
  register,
  errors,
  watch,
  setValue,
  clearErrors
}) => {
  const activityDate = watch('activity_date');

  return (
    <div className="flex-1">
      <p className="font-bold mb-2">Activity Details</p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <InputField
          label="Activity"
          placeholder="Enter activity..."
          className="md:col-span-1"
          name="activity_name"
          type="text"
          register={register}
          error={errors.activity_name}
          size="sm"
        />

        <div className="md:col-span-1">
          <div className="flex items-center gap-2 md:mb-[0.45rem] mb-2">
            <label className="text-[#495057] font-medium text-xs md:text-sm block">Description</label>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button type="button" className="text-primary font-bold text-xs">
                    ?
                  </button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Briefly describe what you worked on so reviewers understand the entry.</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
          <TextAreaField
            name="description"
            label=""
            placeholder="Describe your activity..."
            register={register}
            error={errors.description}
          />
        </div>

        <DatePickerField
          label="Activity date"
          register={register}
          name="activity_date"
          value={activityDate}
          onChange={(date) => {
            setValue('activity_date', date);
            clearErrors('activity_date');
          }}
          className=""
          error={errors.activity_date}
          size="sm"
          tooltipText="The calendar date when this work was performed."
          placeholder="Select date"
          maxDate={new Date()}
        />

        <div className="md:col-span-1">
          <label className="inline-block text-[#495057] font-medium text-xs md:mb-[0.45rem] md:text-sm mb-2">
            Duration
          </label>
          <div className="flex items-start gap-2">
            <InputField
              name="duration_hours"
              placeholder="e.g 8"
              type="number"
              register={register}
              error={errors.duration_hours}
              size="sm"
              className="flex-1"
              unitDescription="Hours"
              min={0}
            />
            <InputField
              name="duration_minutes"
              placeholder="e.g 30"
              type="number"
              register={register}
              error={errors.duration_minutes}
              size="sm"
              className="flex-1"
              unitDescription="Minutes"
              min={0}
              max={59}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateWorkActivityStep;
